import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import {
    CCNL_OPTIONS, TIPOLOGIA_OPTIONS, REGIONI_ITALIA,
    TIPOLOGIE_A_REGOLE, PARAMETRI_GRUPPI, UNITA_LABEL, SOSTITUTIVA_OPTIONS, MESI_OPTIONS
} from "../constants";
import RisultatoRegole from "./RisultatoRegole";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const formatEur = (value) => {
    if (value == null || value === "") return "-";
    const n = Number(value);
    if (Number.isNaN(n)) return "-";
    return `${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" })} €`;
};

const CAMPI_PARAMETRI = PARAMETRI_GRUPPI.flatMap(g => g.campi);
// Di default la busta e' quella del mese corrente
const meseCorrente = () => MESI_OPTIONS[new Date().getMonth() + 1];

// Parametri: nel form si mostrano le percentuali (27,9), il backend vuole le frazioni (0.279).
const parametriPerForm = (valori) => Object.fromEntries(
    CAMPI_PARAMETRI
        .filter(c => valori[c.key] != null)
        .map(c => [c.key, String(c.unita === "perc" ? Number((valori[c.key] * 100).toFixed(4)) : valori[c.key])])
);
const parametriPerBackend = (form) => Object.fromEntries(
    CAMPI_PARAMETRI
        .filter(c => form[c.key] !== "" && form[c.key] != null)
        .map(c => [c.key, c.unita === "perc" ? Number(form[c.key]) / 100 : Number(form[c.key])])
);

const FormSimulazione = ({ editingRecord, onReset, onSavedOrUpdated }) => {
    // Anagrafica
    const [nome, setNome] = useState("");
    const [cognome, setCognome] = useState("");
    const [dataNascita, setDataNascita] = useState("");
    const [note, setNote] = useState("");

    // Input contratto (comuni)
    const [ccnl, setCcnl] = useState(null);
    const [tipologia, setTipologia] = useState(TIPOLOGIA_OPTIONS[0]);

    // Input calcolo a regole (tipologia Standard)
    const [livello, setLivello] = useState(null);
    const [oreSettimanali, setOreSettimanali] = useState("");
    const [scatti, setScatti] = useState("0");
    const [superminimo, setSuperminimo] = useState("");
    const [altreIndennita, setAltreIndennita] = useState("");
    const [lordoFullTime, setLordoFullTime] = useState("");
    const [sostitutiva5, setSostitutiva5] = useState(SOSTITUTIVA_OPTIONS[0]);
    const [meseBusta, setMeseBusta] = useState(meseCorrente);
    const [parametri, setParametri] = useState({});
    const [mostraParametri, setMostraParametri] = useState(false);

    // Input stima AI (altre tipologie)
    const [ral, setRal] = useState("");
    const [regione, setRegione] = useState(null);

    // Configurazione dal backend: livelli CCNL e parametri di default
    const [config, setConfig] = useState(null);

    // Stato UI
    const [loading, setLoading] = useState(false);
    const [errore, setErrore] = useState("");
    const [risultato, setRisultato] = useState(null);
    // Barra verde "simulazione salvata": compare solo dopo Calcola/Aggiorna, non aprendo dallo storico
    const [conferma, setConferma] = useState(null);
    const risultatoRef = useRef(null);

    const editingId = editingRecord?.id || null;
    const isRegole = TIPOLOGIE_A_REGOLE.includes(tipologia?.value || "Standard");
    const ccnlConTabella = config ? config.ccnlConTabella.includes(ccnl?.value) : true;

    const livelloOptions = useMemo(() => (
        config ? Object.entries(config.tabellaLivelli).map(([codice, riga]) => ({
            value: codice,
            label: `${codice} — ${formatEur(riga.lordo)} a tempo pieno`
        })) : []
    ), [config]);

    // Calcolo a regole: le 21 voci della tabella addizionali MEF (Trento e Bolzano separate)
    const regioniOptions = useMemo(() => (
        isRegole && config
            ? config.regioniAddizionale.map(r => ({ value: r, label: r }))
            : REGIONI_ITALIA
    ), [isRegole, config]);

    const parametriDefaultForm = useMemo(
        () => (config ? parametriPerForm(config.parametriDefault) : {}),
        [config]
    );

    // Carica livelli e parametri di default una volta sola
    useEffect(() => {
        const token = localStorage.getItem("token");
        axios.get(`${API_URL}/ai-agents/simulatore-config`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => setConfig(res.data))
            .catch(() => setErrore("Impossibile caricare la configurazione del simulatore"));
    }, []);

    const resetCampi = () => {
        setNome(""); setCognome(""); setDataNascita(""); setNote("");
        setCcnl(null); setTipologia(TIPOLOGIA_OPTIONS[0]);
        setLivello(null); setOreSettimanali(""); setScatti("0");
        setSuperminimo(""); setAltreIndennita(""); setLordoFullTime("");
        setSostitutiva5(SOSTITUTIVA_OPTIONS[0]);
        setMeseBusta(meseCorrente());
        setParametri(parametriDefaultForm);
        setRal(""); setRegione(null);
    };

    // Appena arriva la config, i parametri non ancora compilati prendono i valori di default
    useEffect(() => {
        setParametri(p => ({ ...parametriDefaultForm, ...p }));
    }, [parametriDefaultForm]);

    // Quando arriva un record da modificare, pre-popola tutto il form
    useEffect(() => {
        if (!editingRecord) {
            resetCampi();
            setRisultato(null); setErrore(""); setConferma(null);
            return;
        }
        setNome(editingRecord.nome_dipendente || "");
        setCognome(editingRecord.cognome_dipendente || "");
        setDataNascita(editingRecord.data_nascita ? editingRecord.data_nascita.substring(0, 10) : "");
        setNote(editingRecord.note || "");
        setCcnl(CCNL_OPTIONS.find(o => o.value === editingRecord.ccnl) || null);
        setTipologia(
            TIPOLOGIA_OPTIONS.find(o => o.value === editingRecord.tipologia_contrattuale)
            || TIPOLOGIA_OPTIONS[0]
        );
        setRal(editingRecord.ral || "");
        setRegione(editingRecord.regione ? { value: editingRecord.regione, label: editingRecord.regione } : null);

        // Input del calcolo a regole: salvati in input_calcolo (formato del service)
        const input = editingRecord.input_calcolo || {};
        setLivello(input.livello ? { value: input.livello, label: input.livello } : null);
        setOreSettimanali(input.oreSettimanali != null ? String(input.oreSettimanali) : "");
        setScatti(String(input.scatti ?? 0));
        setSuperminimo(input.superminimo ? String(input.superminimo) : "");
        setAltreIndennita(input.altreIndennita ? String(input.altreIndennita) : "");
        setLordoFullTime(input.lordoFullTime != null ? String(input.lordoFullTime) : "");
        setSostitutiva5(SOSTITUTIVA_OPTIONS.find(o => o.value === (
            input.sostitutiva5 === true ? "si" : input.sostitutiva5 === false ? "no" : "auto"
        )));
        setMeseBusta(MESI_OPTIONS.find(o => o.value === (input.mese || 0)));
        setParametri({ ...parametriDefaultForm, ...parametriPerForm(input.parametri || {}) });

        setRisultato(editingRecord);
        setErrore("");
        setConferma(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingRecord]);

    // Quando arriva la config, l'etichetta del livello in modifica diventa quella completa
    useEffect(() => {
        if (livello && !livello.label.includes("—")) {
            const opt = livelloOptions.find(o => o.value === livello.value);
            if (opt) setLivello(opt);
        }
    }, [livelloOptions, livello]);

    // Il risultato compare sotto il form, spesso fuori schermo: dopo il salvataggio ci si scorre sopra
    useEffect(() => {
        if (conferma && risultatoRef.current) {
            risultatoRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [conferma]);

    const noteCalcolo = useMemo(() => {
        if (!risultato || !risultato.raw_llm_response) return null;
        const raw = risultato.raw_llm_response;
        return typeof raw === "object" ? raw.note_calcolo : null;
    }, [risultato]);

    const validate = () => {
        if (!nome.trim()) return "Inserisci il nome del dipendente";
        if (!ccnl) return "Seleziona un CCNL";
        if (isRegole) {
            if (!(Number(oreSettimanali) > 0)) return "Inserisci le ore settimanali";
            if (ccnlConTabella && !livello) return "Seleziona il livello";
            if (!ccnlConTabella && !(Number(lordoFullTime) > 0)) {
                return "Per questo CCNL inserisci il lordo mensile a tempo pieno";
            }
            if (!regione) return "Seleziona la regione di residenza (serve per l'addizionale regionale)";
            return null;
        }
        if (!ral || Number(ral) < 0) return "Inserisci una RAL valida (>= 0)";
        if (!regione) return "Seleziona una regione";
        return null;
    };

    const handleSubmit = async () => {
        const errMsg = validate();
        if (errMsg) { setErrore(errMsg); return; }

        setLoading(true);
        setErrore("");
        setConferma(null);
        try {
            const token = localStorage.getItem("token");
            const comuni = {
                nome_dipendente: nome.trim(),
                cognome_dipendente: cognome.trim() || null,
                data_nascita: dataNascita || null,
                note: note.trim() || null,
                ccnl: ccnl.value,
                tipologia_contrattuale: tipologia ? tipologia.value : null
            };
            const body = isRegole
                ? {
                    ...comuni,
                    livello: ccnlConTabella ? livello.value : null,
                    lordo_full_time: ccnlConTabella ? null : parseFloat(lordoFullTime),
                    sostitutiva_5: sostitutiva5.value === "si" ? true : sostitutiva5.value === "no" ? false : null,
                    mese: meseBusta.value || null,
                    ore_settimanali: parseFloat(oreSettimanali),
                    scatti: parseInt(scatti || "0", 10),
                    superminimo: parseFloat(superminimo || "0"),
                    altre_indennita: parseFloat(altreIndennita || "0"),
                    regione: regione.value,
                    parametri: parametriPerBackend(parametri)
                }
                : { ...comuni, ral: parseFloat(ral), regione: regione.value };
            const headers = { Authorization: `Bearer ${token}` };

            let res;
            if (editingId) {
                res = await axios.put(`${API_URL}/ai-agents/simulazioni/${editingId}`, body, { headers });
            } else {
                res = await axios.post(`${API_URL}/ai-agents/simula-costo`, body, { headers });
            }
            const salvata = res.data.data;
            setRisultato(salvata);
            setConferma(`${editingId ? "Simulazione aggiornata" : "Simulazione salvata nello storico"} — ${
                [salvata.nome_dipendente, salvata.cognome_dipendente].filter(Boolean).join(" ")}`);
            if (onSavedOrUpdated) onSavedOrUpdated();

            // Dopo una nuova simulazione (POST), pulisco i campi del form per il prossimo
            // calcolo. Lascio visibile il risultato come feedback dell'ultima esecuzione.
            // In modalita' modifica (PUT) non svuoto: l'utente sta verificando l'effetto.
            if (!editingId) resetCampi();
        } catch (e) {
            setErrore(e.response?.data?.error || "Errore durante la simulazione");
        } finally {
            setLoading(false);
        }
    };

    const handleNuova = () => {
        if (onReset) onReset();
    };

    const handleSvuota = () => {
        resetCampi();
        setRisultato(null); setErrore(""); setConferma(null);
        if (editingId && onReset) onReset();
    };

    const campoNumero = (label, value, setValue, { required = false, placeholder = "", step = "0.01" } = {}) => (
        <div className="form-field">
            <label>{label} {required && <span className="required">*</span>}</label>
            <input
                type="number"
                min="0"
                step={step}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={placeholder}
            />
        </div>
    );

    return (
        <div className="simulatore-form-wrapper">
            <div className="simulatore-form-toolbar">
                <button type="button" className="btn-svuota" onClick={handleSvuota}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                    Svuota
                </button>
            </div>

            <div className="form-section">
                <h3 className="form-section-title">Anagrafica dipendente</h3>
                <div className="form-grid">
                    <div className="form-field">
                        <label>Nome <span className="required">*</span></label>
                        <input
                            type="text"
                            value={nome}
                            onChange={(e) => setNome(e.target.value)}
                            placeholder="es. Mario"
                        />
                    </div>
                    <div className="form-field">
                        <label>Cognome</label>
                        <input
                            type="text"
                            value={cognome}
                            onChange={(e) => setCognome(e.target.value)}
                            placeholder="es. Rossi"
                        />
                    </div>
                    <div className="form-field">
                        <label>Data di nascita</label>
                        <input
                            type="date"
                            value={dataNascita}
                            onChange={(e) => setDataNascita(e.target.value)}
                        />
                    </div>
                    <div className="form-field form-field-full">
                        <label>Note</label>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Annotazioni libere (opzionale)"
                            rows={2}
                        />
                    </div>
                </div>
            </div>

            <div className="form-section">
                <h3 className="form-section-title">Dati contrattuali</h3>
                <div className="form-grid">
                    <div className="form-field">
                        <label>CCNL <span className="required">*</span></label>
                        <Select
                            classNamePrefix="filter-select"
                            options={CCNL_OPTIONS}
                            value={ccnl}
                            onChange={setCcnl}
                            placeholder="Seleziona CCNL"
                            isSearchable
                            noOptionsMessage={() => "Nessuna opzione"}
                        />
                    </div>
                    <div className="form-field">
                        <label>Tipologia contrattuale</label>
                        <Select
                            classNamePrefix="filter-select"
                            options={TIPOLOGIA_OPTIONS}
                            value={tipologia}
                            onChange={setTipologia}
                            placeholder="Seleziona tipologia"
                            isSearchable={false}
                            noOptionsMessage={() => "Nessuna opzione"}
                        />
                    </div>

                    {isRegole ? (
                        <>
                            {ccnlConTabella ? (
                                <div className="form-field">
                                    <label>Livello <span className="required">*</span></label>
                                    <Select
                                        classNamePrefix="filter-select"
                                        options={livelloOptions}
                                        value={livello}
                                        onChange={setLivello}
                                        placeholder="Seleziona livello"
                                        isSearchable
                                        noOptionsMessage={() => "Nessuna opzione"}
                                    />
                                </div>
                            ) : (
                                campoNumero("Lordo mensile a tempo pieno (EUR)", lordoFullTime, setLordoFullTime,
                                    { required: true, placeholder: "es. 1800" })
                            )}
                            {campoNumero("Ore settimanali", oreSettimanali, setOreSettimanali,
                                { required: true, placeholder: "es. 30 (tempo pieno 38)", step: "0.5" })}
                            {campoNumero("Numero di scatti", scatti, setScatti, { step: "1" })}
                            {campoNumero("Superminimo mensile (EUR, a tempo pieno)", superminimo, setSuperminimo,
                                { placeholder: "0" })}
                            {campoNumero("Altre indennità mensili (EUR, a tempo pieno)", altreIndennita, setAltreIndennita,
                                { placeholder: "0" })}
                        </>
                    ) : (
                        campoNumero("RAL (EUR)", ral, setRal, { required: true, placeholder: "es. 30000" })
                    )}

                    <div className="form-field">
                        <label>
                            Regione di residenza <span className="required">*</span>
                            {isRegole && <span className="label-hint"> — per l'addizionale regionale 2026</span>}
                        </label>
                        <Select
                            classNamePrefix="filter-select"
                            options={regioniOptions}
                            value={regione}
                            onChange={setRegione}
                            placeholder="Seleziona regione"
                            isSearchable
                            noOptionsMessage={() => "Nessuna opzione"}
                        />
                    </div>

                    {isRegole && (
                        <div className="form-field">
                            <label>
                                Tassa al 5% sugli aumenti del rinnovo CCNL
                                <span className="label-hint"> — L. 199/2025</span>
                            </label>
                            <Select
                                classNamePrefix="filter-select"
                                options={SOSTITUTIVA_OPTIONS}
                                value={sostitutiva5}
                                onChange={setSostitutiva5}
                                isSearchable={false}
                            />
                        </div>
                    )}

                    {isRegole && (
                        <div className="form-field">
                            <label>
                                Mese della busta
                                <span className="label-hint"> — detrazioni sui giorni del mese, 13ª a dicembre</span>
                            </label>
                            <Select
                                classNamePrefix="filter-select"
                                options={MESI_OPTIONS}
                                value={meseBusta}
                                onChange={setMeseBusta}
                                isSearchable={false}
                            />
                        </div>
                    )}
                </div>

                {isRegole && !ccnlConTabella && ccnl && (
                    <p className="simulatore-avviso">
                        Per questo CCNL non è disponibile la tabella dei livelli: il risultato è indicativo
                        (contributi e mensilità aggiuntive vanno verificati nei parametri avanzati).
                    </p>
                )}
                {!isRegole && (
                    <p className="simulatore-avviso">
                        Questa tipologia non è un rapporto subordinato standard: la simulazione è una stima
                        dell'intelligenza artificiale, non un calcolo a regole.
                    </p>
                )}
            </div>

            {isRegole && (
                <div className="form-section">
                    <button
                        type="button"
                        className="parametri-toggle"
                        onClick={() => setMostraParametri(v => !v)}
                        aria-expanded={mostraParametri}
                    >
                        <span>Parametri avanzati</span>
                        <span className="parametri-toggle-hint">
                            {mostraParametri ? "Nascondi" : "Aliquote, addizionali e monte ore — valori del file di riferimento"}
                        </span>
                        <svg className={`parametri-chevron ${mostraParametri ? "aperto" : ""}`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>

                    {mostraParametri && (
                        <div className="parametri-body">
                            {PARAMETRI_GRUPPI.map(gruppo => (
                                <div key={gruppo.titolo} className="parametri-gruppo">
                                    <h4 className="parametri-gruppo-titolo">{gruppo.titolo}</h4>
                                    <div className="parametri-grid">
                                        {gruppo.campi.map(campo => (
                                            <div key={campo.key} className="form-field">
                                                <label>{campo.label}</label>
                                                <div className="input-con-unita">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="any"
                                                        value={parametri[campo.key] ?? ""}
                                                        onChange={(e) => setParametri(p => ({ ...p, [campo.key]: e.target.value }))}
                                                    />
                                                    {UNITA_LABEL[campo.unita] && (
                                                        <span className="unita">{UNITA_LABEL[campo.unita]}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                className="btn-mini"
                                onClick={() => setParametri(parametriDefaultForm)}
                            >
                                Ripristina valori predefiniti
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div className="simulatore-actions">
                <button
                    className="btn-upload"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <span className="loading-spinner"></span>
                            Calcolo in corso...
                        </>
                    ) : editingId ? "Aggiorna Simulazione" : "Calcola e Salva"}
                </button>

                {editingId && (
                    <button className="btn-reset" onClick={handleNuova} disabled={loading}>
                        Nuova Simulazione
                    </button>
                )}
            </div>

            {errore && (
                <div className="upload-message error">
                    {errore}
                </div>
            )}

            <div ref={risultatoRef} className="simulatore-risultato-ancora">
                {conferma && (
                    <div className="simulatore-conferma" role="status">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {conferma}
                    </div>
                )}

                {risultato && risultato.metodo_calcolo === "regole" && <RisultatoRegole risultato={risultato} />}

                {risultato && risultato.metodo_calcolo !== "regole" && (
                    <div className="ai-risultato simulatore-risultato">
                        <h4 className="ai-risultato-title">
                            Risultato simulazione
                            <span className="badge-metodo badge-llm">Stima AI</span>
                            {risultato.created_by_username && (
                                <span className="risultato-creato-da">
                                    {" — creato da "}{risultato.created_by_username}
                                </span>
                            )}
                        </h4>

                        <div className="ai-risultato-grid simulatore-risultato-grid">
                            <div className="ai-risultato-item">
                                <span className="ai-risultato-label">RAL</span>
                                <span className="ai-risultato-value">{formatEur(risultato.ral)}</span>
                            </div>
                            <div className="ai-risultato-item">
                                <span className="ai-risultato-label">Stipendio netto mensile</span>
                                <span className="ai-risultato-value ai-importo">
                                    {formatEur(risultato.stipendio_netto_mensile)}
                                </span>
                            </div>
                            <div className="ai-risultato-item">
                                <span className="ai-risultato-label">Costo azienda annuo</span>
                                <span className="ai-risultato-value ai-importo">
                                    {formatEur(risultato.costo_azienda_annuo)}
                                </span>
                            </div>
                            <div className="ai-risultato-item">
                                <span className="ai-risultato-label">Contributi INPS</span>
                                <span className="ai-risultato-value">{formatEur(risultato.contributi_inps)}</span>
                            </div>
                            <div className="ai-risultato-item">
                                <span className="ai-risultato-label">Contributi INAIL</span>
                                <span className="ai-risultato-value">{formatEur(risultato.contributi_inail)}</span>
                            </div>
                            <div className="ai-risultato-item">
                                <span className="ai-risultato-label">TFR</span>
                                <span className="ai-risultato-value">{formatEur(risultato.tfr)}</span>
                            </div>
                            <div className="ai-risultato-item">
                                <span className="ai-risultato-label">Ratei 13ª</span>
                                <span className="ai-risultato-value">{formatEur(risultato.ratei_13)}</span>
                            </div>
                            <div className="ai-risultato-item">
                                <span className="ai-risultato-label">Ratei 14ª</span>
                                <span className="ai-risultato-value">{formatEur(risultato.ratei_14)}</span>
                            </div>
                            <div className="ai-risultato-item">
                                <span className="ai-risultato-label">IRPEF</span>
                                <span className="ai-risultato-value">{formatEur(risultato.irpef)}</span>
                            </div>
                            <div className="ai-risultato-item">
                                <span className="ai-risultato-label">Addizionale regionale</span>
                                <span className="ai-risultato-value">{formatEur(risultato.addizionale_regionale)}</span>
                            </div>
                        </div>

                        {noteCalcolo && (
                            <div className="simulatore-note-calcolo">
                                <span className="note-calcolo-label">Note di calcolo:</span>
                                <span className="note-calcolo-text">{noteCalcolo}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FormSimulazione;
